import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Film,
  Upload,
  FileVideo,
  ArrowLeft,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Play,
  LogIn,
  Clipboard,
  X,
  CheckCircle2,
} from 'lucide-react';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import { useRoomStore } from '@/store/roomStore';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { AuthModal } from '@/components/auth/AuthModal';
import { UserNav } from '@/components/auth/UserNav';
import { PWAInstallButton } from '@/components/common/PWAInstallButton';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { generateRoomId, generateParticipantId, formatBytes, formatTime } from '@/lib/utils';
import { extractYouTubeVideoId, fetchYouTubeMeta, type YouTubeMeta } from '@/lib/youtube';

export function CreateRoomPage() {
  const navigate = useNavigate();
  const { setRoom, setParticipant } = useRoomStore();
  const { user, isAuthenticated, isApprovedHost } = useAuth();

  const [name, setName] = useState('');
  const [mediaSource, setMediaSource] = useState<'local' | 'youtube'>('local');
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [fileWarning, setFileWarning] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [youtubeInput, setYoutubeInput] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [youtubeMeta, setYoutubeMeta] = useState<YouTubeMeta | null>(null);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  // Auto-fill host name when signed in
  useEffect(() => {
    if (user && !name) {
      setName(user.displayName || user.email?.split('@')[0] || '');
    }
  }, [user, name]);

  const handleFile = useCallback((f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (ext === 'mkv' || ext === 'avi') {
      setFileWarning('Tip: MKV and AVI playback depends on browser support. For the smoothest experience, MP4 or WebM is recommended.');
    } else {
      setFileWarning(null);
    }
    setFile(f);

    const url = URL.createObjectURL(f);
    if (videoPreviewRef.current) {
      videoPreviewRef.current.src = url;
      videoPreviewRef.current.onloadedmetadata = () => {
        setDuration(videoPreviewRef.current?.duration || null);
      };
    }
  }, []);

  const handleYouTubeInputChange = useCallback(async (value: string) => {
    setYoutubeInput(value);
    setYoutubeError(null);
    const clean = value.trim();
    if (!clean) {
      setYoutubeVideoId(null);
      setYoutubeMeta(null);
      return;
    }

    const id = extractYouTubeVideoId(clean);
    if (!id) {
      setYoutubeVideoId(null);
      setYoutubeMeta(null);
      setYoutubeError('Please enter a valid YouTube video URL or 11-character video ID.');
      return;
    }

    setYoutubeVideoId(id);
    setYoutubeLoading(true);
    try {
      const meta = await fetchYouTubeMeta(id);
      setYoutubeMeta(meta);
    } catch {
      // fallback
    } finally {
      setYoutubeLoading(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile]
  );

  const doCreateRoom = (currentUserUid: string, nameToUse: string, fileToUse: File) => {
    setError(null);
    setIsCreating(true);

    try {
      const roomId = generateRoomId();
      const participantId = generateParticipantId();

      setParticipant(participantId, nameToUse.trim(), 'host');
      setRoom({
        roomId,
        hostId: participantId,
        hostUid: currentUserUid,
        mediaType: 'local',
        movieTitle: fileToUse.name.replace(/\.[^/.]+$/, ''),
        movieDuration: duration || 0,
        playbackState: 'paused',
        playbackRate: 1,
        currentTime: 0,
        lastActionTime: Date.now(),
        lastActionBy: participantId,
        version: 0,
        participants: [
          {
            id: participantId,
            name: nameToUse.trim(),
            role: 'host',
            joinedAt: Date.now(),
            connectionState: 'idle',
          },
        ],
        createdAt: Date.now(),
      });

      (window as Window & { __cinesync_file?: File }).__cinesync_file = fileToUse;
      navigate(`/room/${roomId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create room');
      setIsCreating(false);
    }
  };

  const doCreateYouTubeRoom = (currentUserUid: string, nameToUse: string, videoId: string, title: string) => {
    setError(null);
    setIsCreating(true);

    try {
      const roomId = generateRoomId();
      const participantId = generateParticipantId();

      setParticipant(participantId, nameToUse.trim(), 'host');
      setRoom({
        roomId,
        hostId: participantId,
        hostUid: currentUserUid,
        mediaType: 'youtube',
        youtubeVideoId: videoId,
        movieTitle: title || `YouTube Video (${videoId})`,
        movieDuration: 0,
        playbackState: 'paused',
        playbackRate: 1,
        currentTime: 0,
        lastActionTime: Date.now(),
        lastActionBy: participantId,
        version: 0,
        participants: [
          {
            id: participantId,
            name: nameToUse.trim(),
            role: 'host',
            joinedAt: Date.now(),
            connectionState: 'idle',
          },
        ],
        createdAt: Date.now(),
      });

      navigate(`/room/${roomId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create room');
      setIsCreating(false);
    }
  };

  const handleCreate = async () => {
    if (mediaSource === 'local' && !file) {
      setError('Please select a video file.');
      return;
    }
    if (mediaSource === 'youtube' && !youtubeVideoId) {
      setError('Please enter a valid YouTube video URL or ID.');
      return;
    }

    if (!isAuthenticated || !user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!isApprovedHost) {
      setError('Host approval required. Please redeem an invitation link from the administrator to create rooms.');
      return;
    }

    const effectiveName = name.trim() || user.displayName || user.email?.split('@')[0] || 'Host';
    if (mediaSource === 'local' && file) {
      doCreateRoom(user.uid, effectiveName, file);
    } else if (mediaSource === 'youtube' && youtubeVideoId) {
      doCreateYouTubeRoom(user.uid, effectiveName, youtubeVideoId, youtubeMeta?.title || 'YouTube Video');
    }
  };

  return (
    <div className="min-h-screen bg-cinema-950 text-cinema-100 flex flex-col font-sans">
      <header className="hairline-b bg-cinema-900/80 px-6 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-xs font-medium text-cinema-300 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp rounded"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>

        <div className="flex items-center gap-2.5 sm:gap-4">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-lamp" />
            <span className="text-xs uppercase tracking-wider text-cinema-300 font-semibold hidden xs:inline">Host a Room</span>
          </div>
          <PWAInstallButton />
          <ThemeToggle showDropdown />
          <div className="hairline-l h-5 border-cinema-700/80 ml-1 hidden sm:block" />
          <UserNav onOpenAuth={() => setIsAuthModalOpen(true)} />
        </div>
      </header>

      <main className="flex-1 max-w-xl w-full mx-auto px-6 py-12">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-cinema-100 tracking-tight">Create a Watch Room</h1>
          <p className="text-sm text-cinema-300 mt-2 leading-relaxed">
            Select your movie and enter your name. Your friends will join via a private link and stream the movie directly from your browser.
          </p>
        </div>

        {/* Host Authentication Status Banner */}
        <div className="mb-6">
          {isAuthenticated && user ? (
            isApprovedHost ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-reel-emerald/10 border border-reel-emerald/20 text-xs text-reel-emerald">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  Approved Host: <strong className="text-cinema-100 font-medium">{user.displayName || user.email}</strong>
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-xs text-amber-300">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="leading-relaxed text-[11px]">
                  <span className="font-semibold text-cinema-100">Host Approval Required: </span>
                  Your account ({user.email}) is awaiting approval. Please redeem an invitation link from the administrator to host rooms.
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-cinema-900 border border-cinema-750 text-xs text-cinema-300">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-4 h-4 text-lamp flex-shrink-0" />
                <span className="truncate">Only approved room hosts can create rooms. Participants join free.</span>
              </div>
              <button
                type="button"
                onClick={() => setIsAuthModalOpen(true)}
                className="text-lamp hover:underline font-semibold flex-shrink-0 text-xs"
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Display Name */}
          <div className="space-y-2">
            <label htmlFor="create-name-input" className="block text-xs font-semibold uppercase tracking-wider text-cinema-300">
              Your Name
            </label>
            <input
              id="create-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex"
              maxLength={24}
              autoFocus
              className="w-full bg-cinema-900 border border-cinema-700 rounded-md px-4 py-3 text-sm text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
            />
          </div>

          {/* Media Source Selector Tabs */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-cinema-300">
              Media Source
            </label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-cinema-900 rounded-lg border border-cinema-700">
              <button
                type="button"
                onClick={() => { setMediaSource('local'); setError(null); }}
                className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                  mediaSource === 'local'
                    ? 'bg-cinema-800 text-cinema-100 shadow-sm border border-cinema-600/60'
                    : 'text-cinema-400 hover:text-cinema-200'
                }`}
              >
                <Film className="w-3.5 h-3.5 text-lamp" />
                <span>Local Movie</span>
              </button>
              <button
                type="button"
                onClick={() => { setMediaSource('youtube'); setError(null); }}
                className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                  mediaSource === 'youtube'
                    ? 'bg-cinema-800 text-cinema-100 shadow-sm border border-cinema-600/60'
                    : 'text-cinema-400 hover:text-cinema-200'
                }`}
              >
                <YouTubeIcon className="w-3.5 h-3.5 text-red-500" />
                <span>YouTube Video</span>
              </button>
            </div>
          </div>

          {mediaSource === 'local' ? (
            /* Film Selection */
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-cinema-300">
                Movie File
              </label>
              
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                  ${isDragging 
                    ? 'border-lamp bg-lamp/10' 
                    : file 
                    ? 'border-cinema-600 bg-cinema-900/90' 
                    : 'border-cinema-700 bg-cinema-900 hover:border-cinema-600'
                  }
                `}
                tabIndex={0}
                role="button"
                aria-label="Select movie file"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mp4,.webm,.mov,.mkv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />

                <video ref={videoPreviewRef} className="hidden" preload="metadata" />

                {file ? (
                  <div className="space-y-3">
                    <div className="w-10 h-10 rounded-md bg-lamp-dim border border-lamp/30 flex items-center justify-center mx-auto text-lamp">
                      <FileVideo className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-cinema-100 truncate">{file.name}</p>
                      <div className="flex items-center justify-center gap-3 text-xs text-cinema-400 mt-1">
                        <span>{formatBytes(file.size)}</span>
                        {duration ? <span>• {formatTime(duration)}</span> : null}
                      </div>
                    </div>
                    <div className="inline-block text-xs text-lamp hover:underline">
                      Click to choose a different file
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 py-4">
                    <Upload className="w-8 h-8 text-cinema-400 mx-auto" />
                    <p className="text-sm font-medium text-cinema-100">Click or drag a movie file here</p>
                    <p className="text-xs text-cinema-400">
                      MP4, WebM, MOV, or MKV
                    </p>
                  </div>
                )}
              </div>

              {fileWarning && (
                <div className="text-xs text-amber-300 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/20 p-3 rounded">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{fileWarning}</span>
                </div>
              )}
            </div>
          ) : (
            /* YouTube URL / ID Selection */
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="create-youtube-input" className="block text-xs font-semibold uppercase tracking-wider text-cinema-300">
                    YouTube URL or Video ID
                  </label>
                  {youtubeVideoId && (
                    <span className="text-[10px] font-mono text-reel-emerald flex items-center gap-1 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Ready to Stream</span>
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="create-youtube-input"
                    type="text"
                    value={youtubeInput}
                    onChange={(e) => handleYouTubeInputChange(e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text') || '';
                      if (text) {
                        e.preventDefault();
                        handleYouTubeInputChange(text.trim());
                      }
                    }}
                    placeholder="Paste YouTube link (watch, youtu.be, shorts, or embed)"
                    className="w-full bg-cinema-900 border border-cinema-700 rounded-md pl-10 pr-24 py-3 text-sm text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                  />
                  <YouTubeIcon className="w-4 h-4 text-red-500 absolute left-3.5 top-3.5 pointer-events-none" />
                  
                  <div className="absolute right-2.5 top-2.5 flex items-center gap-1.5">
                    {youtubeInput ? (
                      <button
                        type="button"
                        onClick={() => handleYouTubeInputChange('')}
                        className="p-1 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
                        title="Clear link"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            if (text) handleYouTubeInputChange(text.trim());
                          } catch {
                            setYoutubeError('Clipboard access denied by browser. Please paste using Ctrl+V or Cmd+V.');
                          }
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-cinema-800 hover:bg-cinema-750 text-cinema-300 hover:text-cinema-100 border border-cinema-700/80 transition-colors shadow-xs"
                        title="Paste from clipboard"
                      >
                        <Clipboard className="w-3.5 h-3.5 text-lamp" />
                        <span>Paste</span>
                      </button>
                    )}
                    {youtubeLoading && (
                      <Loader2 className="w-4 h-4 text-lamp animate-spin ml-1" />
                    )}
                  </div>
                </div>
                {youtubeError && (
                  <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1.5 animate-fade-in">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{youtubeError}</span>
                  </p>
                )}
              </div>

              {/* YouTube Video Preview Card */}
              {youtubeMeta && youtubeVideoId && (
                <div className="p-3.5 rounded-lg bg-cinema-900 border border-cinema-700 flex items-center gap-3.5 animate-fade-in shadow-xs">
                  <img
                    src={youtubeMeta.thumbnailUrl}
                    alt={youtubeMeta.title}
                    className="w-24 h-14 object-cover rounded bg-cinema-950 flex-shrink-0 border border-cinema-700/60"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-cinema-100 truncate" title={youtubeMeta.title}>
                      {youtubeMeta.title}
                    </p>
                    <p className="text-xs text-cinema-400 truncate mt-0.5">
                      {youtubeMeta.authorName} · Official YouTube Player
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-cinema-400">
                Synchronized playback using the official YouTube Player API. No video file download required.
              </p>
            </div>
          )}

          {/* Privacy Note */}
          <div className="p-4 rounded-lg bg-cinema-900/50 border border-cinema-700/60 flex items-start gap-3 text-xs text-cinema-300">
            <ShieldCheck className="w-4 h-4 text-reel-emerald flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              {mediaSource === 'local'
                ? 'Your movie file remains entirely on your computer. Guests stream directly from your browser without uploading to any cloud server.'
                : 'YouTube videos stream directly from YouTube via the official IFrame API with synchronized room controls.'}
            </p>
          </div>

          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleCreate}
            disabled={
              isCreating ||
              (mediaSource === 'local' ? !file : !youtubeVideoId) ||
              (isAuthenticated && !isApprovedHost)
            }
            className="w-full py-3.5 px-4 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 font-bold text-sm tracking-wide transition-all duration-150 enabled:active:scale-[0.96] shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Creating room…</span>
              </>
            ) : isAuthenticated ? (
              isApprovedHost ? (
                <>
                  <Play className="w-4 h-4 fill-current" />
                  <span>Create Watch Room</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4" />
                  <span>Host Approval Required</span>
                </>
              )
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>Sign in to Create Room</span>
              </>
            )}
          </button>
        </div>
      </main>

      {/* Host Authentication Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={() => {
          setIsAuthModalOpen(false);
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            const nameToUse = name.trim() || currentUser.displayName || currentUser.email?.split('@')[0] || 'Host';
            if (mediaSource === 'local' && file) {
              doCreateRoom(currentUser.uid, nameToUse, file);
            } else if (mediaSource === 'youtube' && youtubeVideoId) {
              doCreateYouTubeRoom(currentUser.uid, nameToUse, youtubeVideoId, youtubeMeta?.title || 'YouTube Video');
            }
          }
        }}
      />
    </div>
  );
}
